import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-inspector>` says about itself.
 *
 * Its purpose line says properties are "edited in place", so the first example puts real editable
 * controls in it rather than a column of read-only rows. That distinction is the whole piece: a
 * panel of static pairs is a DetailSection that happens to be at the side, and if this one is drawn
 * that way in the only place a person looks it up, they will build the editing somewhere else.
 *
 * It is a shell around whatever it is given — a padded head with a full-bleed divider under it, and
 * a padded body — so what goes inside is the caller's field set, composed from the field pieces the
 * library already has. The examples do exactly that.
 *
 * ON `modal`. It puts this panel INSIDE `<caos-overlay>` (CAOS-429) rather than doing that
 * element's work again. The scrim, the role="dialog" with aria-modal, the focus that moves in on
 * open, the Tab trap and the focus handed back to whoever opened it all come from the overlay; this
 * piece keeps the header, the body and its own close event. It used to re-implement half of that
 * and skip the rest — most visibly the focus return, which it did not do and the overlay always
 * did. There is still no example of the modal variant here for a mechanical reason: it takes focus
 * the moment it is connected, and an example that yanks the page to itself the instant it is drawn
 * is a bad neighbour on a page of forty examples.
 */
const docs: ComponentDocs = {
  aliases: ['properties panel', 'property inspector', 'details pane', 'side panel', 'attribute editor', 'settings panel'],

  answers: [
    'show the properties of whatever is selected',
    'edit a record without leaving the surface it is on',
    'put a properties panel beside a canvas or a board',
    'give a builder somewhere to change the thing being built',
  ],

  // Beside the work, on the surface that holds the selection. Not a home page — nothing is selected
  // on one — and not the frame, which owns no selection of its own.
  placements: ['recordPage', 'appPage'],

  inputs: [
    {
      name: 'heading',
      attribute: 'heading',
      type: 'text',
      description:
        'What the panel is about, usually the name of the selected thing. It is also what names the ' +
        'panel for a screen reader, so a panel with no heading is an unlabelled region.',
    },
    {
      name: 'closable',
      attribute: 'closable',
      type: 'boolean',
      description:
        'Show the close control in the header. It only REQUESTS the close — the panel does not ' +
        'remove itself, so the surface that owns the panel decides what closing means.',
      default: false,
    },
    {
      name: 'modal',
      attribute: 'modal',
      type: 'boolean',
      description:
        'Put it inside an Overlay rather than leaving it a complementary landmark: it floats over a ' +
        'scrim, takes focus when it is connected, traps Tab, closes on Escape and on a scrim click, ' +
        'and hands focus back to whatever opened it. The dialog is labelled with the heading ' +
        'and it always shows the close control.',
      default: false,
    },
  ],

  events: [
    {
      name: 'caos-inspector-close',
      when:
        'The close control is pressed, or — while it is modal — Escape is pressed or the scrim is ' +
        'clicked. However it was asked for, it arrives under this one name. It is a request and it ' +
        'is cancelable — the panel never removes itself.',
      carries: 'nothing',
    },
  ],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The body: the properties of the selection. The panel pads it, so what goes in is the fields ' +
        'themselves — field renderers, key-value rows, detail sections — and not a padded wrapper.',
    },
    {
      name: 'actions',
      takes: 'Controls that belong to the panel as a whole, sitting in the header beside the heading.',
    },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The fill of the panel and of its header.' },
    { token: '--caos-color-surface-2', controls: 'The hover fill of the close control.' },
    { token: '--caos-color-border', controls: 'The panel edge and the divider under the header.' },
    { token: '--caos-color-text', controls: 'The heading text.' },
    { token: '--caos-color-text-muted', controls: 'The resting colour of the close control.' },
    { token: '--caos-shadow-float', controls: 'The elevation of the modal variant, drawn by the Overlay it sits in. A plain panel does not float and has none.' },
    { token: '--caos-radius-lg', controls: 'The corner radius of the panel.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of the close control.' },
    { token: '--caos-focus-ring', controls: 'The ring on the close control when it is reached by keyboard.' },
    { token: '--caos-font-display', controls: 'The typeface the heading is set in.' },
    { token: '--caos-space-2', controls: 'The gap between the controls in the actions slot.' },
    { token: '--caos-space-3', controls: 'The header padding and the gap between heading, actions and close.' },
    { token: '--caos-space-4', controls: 'The padding of the body and the horizontal padding of the header.' },
  ],

  parts: [
    { part: 'panel', is: 'the panel' },
    { part: 'header', is: 'the header strip' },
    { part: 'title', is: 'the heading' },
    { part: 'actions', is: 'the action group in the header' },
    { part: 'close', is: 'the close control' },
    { part: 'body', is: 'the scrolling body' },
  ],

  methods: [
    {
      name: 'requestClose',
      description:
        'Raise the close request from code, the same way the close control and Escape do — for a Done ' +
        'button in the actions slot, so one path decides what closing means.',
      args: [],
    },
  ],

  placing:
    'It goes in a column beside the thing it describes — the right-hand pane of a split surface, or ' +
    'a fixed side column of a builder — and it takes the width of that column. It fills the height ' +
    'available to it and scrolls its own body, so it wants a parent with a height rather than one ' +
    'that grows with its content. What goes inside belongs to the caller: the panel brings the head, ' +
    'the divider and the padding, and nothing else.',

  whenToUse:
    'Something is selected and its properties are edited right there, with the surface that holds ' +
    'the selection still visible and still in play behind the panel.',

  insteadUse: [
    {
      name: 'DetailSection',
      when: 'The fields belong in the body of the record page itself, in the section the layout puts them in.',
    },
    {
      name: 'Overlay',
      when:
        'The panel has to take the screen: a scrim over the page, a centred or edge placement, and ' +
        'focus handed back to whatever opened it.',
    },
    {
      name: 'SplitView',
      when: 'You are laying out the two panes themselves — a list on the left and the record on the right.',
    },
  ],

  accessibility:
    'The panel is a role="complementary" landmark named by its heading, so it is reachable as a ' +
    'region and announced for what it is. Set modal and it goes inside an Overlay, which is the ' +
    'role="dialog" with aria-modal: focus moves in on open and Tab is trapped within it, piercing ' +
    'slotted custom elements so a button whose real control lives in its own shadow root is still ' +
    'in the ring, and focus returns to whatever opened it on close; Escape raises the ' +
    'close. The close control is a real button with an accessible name. What is left to the author: ' +
    'give it a heading and act on the close event, which is the one name every dismissal arrives ' +
    'under — the close control, Escape, or the scrim.',

  examples: [
    {
      key: 'properties',
      label: 'Properties of a selection',
      summary:
        'The panel doing what its purpose line says: the fields of the selected record as editable ' +
        'controls, with the one value nobody edits left as a plain row.',
      placement: {
        component: 'caos-inspector',
        inputs: { heading: 'ORD-4471', closable: true },
        children: [
          { component: 'caos-key-value-row', inputs: { label: 'Reference', value: 'ORD-4471' } },
          {
            component: 'caos-field-renderer',
            inputs: {
              mode: 'edit',
              // An object cannot go in an attribute, so this is set as a property — the same shape a
              // surface hands the renderer from the object's own layout.
              field: { storageType: 'Text', label: 'Account', required: true },
              value: 'Meridian Supply',
            },
          },
          {
            component: 'caos-field-renderer',
            inputs: { mode: 'edit', field: { storageType: 'Text', label: 'Stage' }, value: 'In review' },
          },
          {
            component: 'caos-field-renderer',
            inputs: {
              mode: 'edit',
              field: { storageType: 'Number', format: 'currency', label: 'Total', scale: 2 },
              value: 12400,
            },
          },
        ],
      },
      host: {
        summary:
          'removes the panel when it reports a close, because the piece raises the request and the ' +
          'surface decides the outcome',
        run: (stage) => {
          const inspector = stage.querySelector('caos-inspector');
          if (!inspector) return;
          inspector.addEventListener('caos-inspector-close', () => inspector.remove());
        },
      },
    },
    {
      key: 'actions',
      label: 'With header actions',
      summary:
        'Controls belonging to the panel as a whole, in the actions slot beside the heading — where ' +
        'they sit apart from the fields and stay put while the body scrolls.',
      placement: {
        component: 'caos-inspector',
        inputs: { heading: 'ORD-4471' },
        children: [
          { component: 'caos-button', inputs: { slot: 'actions', variant: 'ghost', size: 'sm' }, children: ['Revert'] },
          { component: 'caos-button', inputs: { slot: 'actions', variant: 'primary', size: 'sm' }, children: ['Save'] },
          { component: 'caos-key-value-row', inputs: { label: 'Account', value: 'Meridian Supply' } },
          { component: 'caos-key-value-row', inputs: { label: 'Total', value: '$12,400' } },
        ],
      },
    },
  ],
};

export default docs;
